import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
// Reuse the trip fallback styling (same layout).
import '../TripLinkFallbackPage/TripLinkFallbackPage.css';

const API_BASE =
  process.env.REACT_APP_APP_BASE_URL ||
  (typeof window !== 'undefined' && window.location.origin.includes('localhost')
    ? 'https://app.voiatravel.com'
    : '');

// Detect the visitor's mobile OS so we can show only the relevant store badge.
function getMobileOS() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/android/i.test(ua)) return 'android';
  // iPhone/iPod/iPad — and iPadOS 13+ which reports as "Macintosh" but has touch.
  if (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return 'other';
}

/**
 * Fallback page for a trip invite link (/i/:token) opened without the app.
 * Shows who invited you + the trip, store buttons, and the invite code as text
 * so a fresh install can enter it manually (DIY deferred-deep-link fallback).
 */
const InviteFallbackPage = () => {
  const { token } = useParams();
  const { state } = useLocation();
  const [invite, setInvite] = useState(() => ({
    tripName: state?.tripName ?? null,
    inviterName: state?.inviterName ?? null,
    inviterUsername: state?.inviterUsername ?? null,
    imageUrl: state?.imageUrl ?? null,
    status: state?.status ?? null,
    expired: state?.expired ?? false,
    kind: null,
    inviterPhoto: null,
    countryCount: null,
  }));
  // A friend's "invite to the ranking" link (f_ prefix) rather than a trip invite.
  const isFriend = /^f_[0-9a-f]{32}$/.test(token || '');
  const [loading, setLoading] = useState(!state?.tripName && !state?.imageUrl);
  const appStoreUrl = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB';
  const playStoreUrl = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.voia.travel.mobile';
  const os = getMobileOS();

  useEffect(() => {
    if (state?.tripName != null || state?.imageUrl != null) return;
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/invite/${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        if (!cancelled) {
          setInvite({
            tripName: data.tripName || null,
            inviterName: data.inviterName || null,
            inviterUsername: data.inviterUsername || null,
            imageUrl: data.imageUrl || null,
            status: data.status || null,
            expired: !!data.expired,
            kind: data.kind || null,
            inviterPhoto: data.inviterPhoto || null,
            countryCount: typeof data.countryCount === 'number' ? data.countryCount : null,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, state?.tripName, state?.imageUrl]);

  const inactive = invite.status === 'revoked' || invite.expired;
  const inviter = invite.inviterUsername
    ? `@${invite.inviterUsername}`
    : invite.inviterName || 'Someone';
  // Mid-sentence fallback stays lowercase ("follow your friend"); the heading has its own.
  const knownName = invite.inviterName || (invite.inviterUsername ? `@${invite.inviterUsername}` : null);
  const friendName = knownName || 'your friend';
  const friendHeading = inactive
    ? 'This invite has expired'
    : !knownName
      ? 'You’re invited to compare countries'
      : invite.countryCount
        ? `${knownName} has been to ${invite.countryCount} ${invite.countryCount === 1 ? 'country' : 'countries'}. Where do you rank?`
        : `${knownName} invited you to compare countries`;
  const headingText = isFriend ? friendHeading : inactive
    ? 'This invite has expired'
    : invite.tripName
      ? `${inviter} invited you to “${invite.tripName}” trip`
      : `${inviter} invited you to a trip`;
  const subtitle = inactive
    ? isFriend
      ? 'This invite link is no longer active. Ask your friend for a new one.'
      : 'This invite link is no longer active. Ask the organizer for a new one.'
    : 'Install Voia to join the trip and start planning together.';
  const imageUrl = isFriend ? invite.inviterPhoto : invite.imageUrl;

  return (
    <div className="trip-fallback-container">
      <main className="trip-fallback-content">
        {loading ? (
          <p className="trip-fallback-description">Loading…</p>
        ) : (
          <>
            <header className="trip-fallback-header">
              {imageUrl && !inactive && (
                <div className="trip-fallback-image-wrap" style={{ margin: '0 auto 1rem' }}>
                  <img
                    src={imageUrl}
                    alt={isFriend ? friendName : invite.tripName || 'Trip'}
                    className="trip-fallback-image"
                    style={isFriend ? { width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' } : undefined}
                  />
                </div>
              )}
              <h1 className="trip-fallback-title">{headingText}</h1>
            </header>

            <div className="trip-fallback-cta-block">
              {inactive ? (
                <p className="trip-fallback-description">{subtitle}</p>
              ) : (
                <>
                  <p
                    className="trip-fallback-description"
                    style={{ margin: '0 auto 0.5rem', textAlign: 'center' }}
                  >
                    {isFriend
                      ? `Get VOIA to follow ${friendName} and compare. Two quick steps:`
                      : 'Don’t have the Voia app yet? Joining takes two quick steps:'}
                  </p>
                  <div
                    style={{
                      textAlign: 'center',
                      maxWidth: 340,
                      margin: '4px auto 0',
                      lineHeight: 1.6,
                      color: '#444',
                      fontSize: 15,
                    }}
                  >
                    <p style={{ marginBottom: 10 }}>
                      <strong>1. Install {isFriend ? 'VOIA' : 'Voia'}</strong>
                    </p>
                    <div className="trip-fallback-stores" style={{ marginBottom: 18 }}>
                      {(os === 'ios' || os === 'other') && (
                        <a
                          href={appStoreUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="trip-fallback-store-link"
                          aria-label="Download on the App Store"
                        >
                          <img
                            src={`${process.env.PUBLIC_URL || ''}/assets/app_store_badge.svg`}
                            alt="Download on the App Store"
                            className="trip-fallback-store-badge"
                          />
                        </a>
                      )}
                      {(os === 'android' || os === 'other') && (
                        <a
                          href={playStoreUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="trip-fallback-store-link"
                          aria-label="Get it on Google Play"
                        >
                          <img
                            src={`${process.env.PUBLIC_URL || ''}/assets/google_play_badge.png`}
                            alt="Get it on Google Play"
                            className="trip-fallback-store-badge"
                          />
                        </a>
                      )}
                    </div>
                    <p style={{ marginBottom: 0 }}>
                      <strong>2. Come back and tap this invite link again</strong>
                      {isFriend
                        ? `. It opens the app so you can follow ${friendName}.`
                        : ' — it opens the app and adds you to the trip.'}
                    </p>
                  </div>
                  <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                    {isFriend
                      ? knownName
                        ? `Already have VOIA? This link opens ${knownName}'s invite directly.`
                        : 'Already have VOIA? This link opens the invite directly.'
                      : 'Already have Voia? This link opens the trip directly — no need to come back.'}
                  </p>
                </>
              )}
              <img
                src={`${process.env.PUBLIC_URL || ''}/og-preview.png`}
                alt="Voia"
                className="trip-fallback-og-preview"
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default InviteFallbackPage;
